/**
 * Worker Profiles Hook
 * React hook for fetching worker user profiles (contractors, freelancers) globally
 * Workers are not tied to specific organizations and are available across the entire system
 */

import { useState, useEffect, useCallback } from 'react'
import { useApi } from '@/lib/api'
import { PROFILE_STATUS, PROFILE_TYPE } from '@/lib/constants/profile-codes'

export interface WorkerProfile {
  Id: number
  ProfileStatusId: number
  ContactId: number
  ProfileTypeId: number
  OrganizationId: number
  T4TypeId?: number | null
  IsEngagementProfile?: boolean | null
  FullName: string
  DisplayValue: string
  // org.CodeLineOfBusiness code, enriched from the People service (Cosmos-sourced) after the
  // SQL list loads. Optional: absent if the worker isn't selectable on the add-worker dropdown
  // (only those ids are enriched), has no LOB, or the enrichment call fails.
  LineOfBusinessCode?: string | null
}

/**
 * Can this worker be offered on the add-worker dropdown?
 *
 * The list itself is fetched wide (the OData filter includes the whole termination lifecycle) so
 * RemovedWorker entries still resolve for display. This is the narrower set that can actually be
 * picked, and it is also exactly the set enriched with Line of Business. Both callers must use
 * this predicate: if only the dropdown widened, the extra rows would silently render without a
 * LOB bracket, because enrichment never asked the People service for those ids.
 *
 * Does not cover "already selected on this engagement" — that's per-dialog state and belongs to
 * the consumer.
 */
export const isSelectableWorkerProfile = (profile: WorkerProfile): boolean =>
  Boolean(profile.IsEngagementProfile)
  && (profile.ProfileStatusId === PROFILE_STATUS.ACTIVE
    || profile.ProfileStatusId === PROFILE_STATUS.PENDING_CHANGE)

// Thin projection returned by People `line-of-business-list` (PascalCase JSON).
interface WorkerLineOfBusinessApiItem {
  UserProfileId: number
  LineOfBusinessCode: string | null
}

interface WorkerProfileApiItem {
  Id: number
  ProfileStatusId: number
  ContactId: number
  ProfileTypeId: number
  OrganizationId: number
  T4TypeId?: number | null
  IsEngagementProfile?: boolean | null
  Contact: {
    FullName: string
  }
}

interface WorkerProfilesApiResponse {
  Items: WorkerProfileApiItem[]
}

interface CachedWorkerProfiles {
  workerProfiles: WorkerProfile[]
  loading: boolean
  error: string | null
  timestamp: number
}

interface UseWorkerProfilesReturn {
  loading: boolean
  error: string | null
  workerProfiles: WorkerProfile[]
  refresh: () => Promise<void>
}

// Global cache for worker profiles
let workerProfilesCache: CachedWorkerProfiles | null = null

// Cache TTL: 5 minutes
const CACHE_TTL = 5 * 60 * 1000

// Set of subscribers for cache updates
const subscribers = new Set<() => void>()

// Notify all subscribers when cache updates
const notifySubscribers = () => {
  subscribers.forEach(callback => callback())
}

export function useWorkerProfiles(): UseWorkerProfilesReturn {
  const { mainApi, peopleApi } = useApi()
  const [, forceUpdate] = useState(0)
  
  // Subscribe to cache updates
  useEffect(() => {
    const updateCallback = () => forceUpdate(prev => prev + 1)
    subscribers.add(updateCallback)
    
    return () => {
      subscribers.delete(updateCallback)
    }
  }, [])

  // Enrich with Line of Business in a single bulk call. LOB lives in Cosmos (owned by the People
  // service), not in the SQL worker list, so it can't be selected inline. Awaited before the list
  // is published so the dropdown never renders a set of rows whose LOB brackets pop in a moment
  // later. Best-effort: a failure leaves LOB undefined rather than breaking the worker list.
  const enrichWithLineOfBusiness = async (profiles: WorkerProfile[]) => {
    // LOB is only ever rendered on the add-worker dropdown, so only the workers that dropdown can
    // offer are worth enriching. See isSelectableWorkerProfile for why both sides share the test.
    const ids = profiles.filter(isSelectableWorkerProfile).map(profile => profile.Id)

    if (ids.length === 0) return

    try {
      const lobItems = await peopleApi.post<WorkerLineOfBusinessApiItem[]>('/people/line-of-business-list', ids)
      const lobByProfileId = new Map((lobItems || []).map(item => [item.UserProfileId, item.LineOfBusinessCode]))

      profiles.forEach(profile => {
        if (lobByProfileId.has(profile.Id)) {
          profile.LineOfBusinessCode = lobByProfileId.get(profile.Id) ?? null
        }
      })
    } catch (error) {
      console.error('Failed to enrich worker profiles with line of business:', error)
    }
  }

  const fetchWorkerProfiles = async (): Promise<WorkerProfile[]> => {
    // Include the full termination lifecycle (PendingDeactivation, Inactive, Terminated) so RemovedWorker
    // entries on engagements resolve for display after the worker profile has moved past Active. Consumers
    // that need to exclude these from selection (e.g. WorkersSection's add-worker dropdown) filter on
    // ProfileStatusId themselves.
    const oDataParams = new URLSearchParams({
      '$expand': 'Contact',
      '$select': 'Id,ProfileStatusId,ProfileTypeId,ContactId,OrganizationId,T4TypeId,IsEngagementProfile,Contact/FullName',
      '$filter': `(ProfileTypeId eq ${PROFILE_TYPE.T4} or ProfileTypeId eq ${PROFILE_TYPE.CANADIAN_INC}) and (ProfileStatusId eq ${PROFILE_STATUS.ACTIVE} or ProfileStatusId eq ${PROFILE_STATUS.PENDING_CHANGE} or ProfileStatusId eq ${PROFILE_STATUS.PENDING_DEACTIVATION} or ProfileStatusId eq ${PROFILE_STATUS.INACTIVE} or ProfileStatusId eq ${PROFILE_STATUS.TERMINATED})`
    })
    
    const endpoint = `api/UserProfile/GetListUserProfileWorker?${oDataParams.toString()}`
    const response = await mainApi.get<WorkerProfilesApiResponse>(endpoint)
    
    const profiles = response?.Items || []
    
    const mappedProfiles: WorkerProfile[] = profiles.map((p: WorkerProfileApiItem) => ({
      Id: p.Id,
      ProfileStatusId: p.ProfileStatusId,
      ContactId: p.ContactId,
      ProfileTypeId: p.ProfileTypeId,
      OrganizationId: p.OrganizationId,
      T4TypeId: p.T4TypeId,
      IsEngagementProfile: p.IsEngagementProfile ?? false,
      FullName: p.Contact?.FullName || 'Unknown',
      DisplayValue: `${p.Contact?.FullName || 'Unknown'} - ${p.Id}`
    }))

    await enrichWithLineOfBusiness(mappedProfiles)

    return mappedProfiles
  }

  const fetchAndCache = useCallback(async () => {
    // Set loading state in cache
    workerProfilesCache = {
      workerProfiles: workerProfilesCache?.workerProfiles || [],
      loading: true,
      error: null,
      timestamp: Date.now()
    }
    notifySubscribers()

    try {
      const workerProfiles = await fetchWorkerProfiles()

      // Update cache with successful result
      workerProfilesCache = {
        workerProfiles,
        loading: false,
        error: null,
        timestamp: Date.now()
      }

      notifySubscribers()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
      console.error('Failed to fetch worker profiles:', errorMessage)
      
      // Update cache with error state
      workerProfilesCache = {
        workerProfiles: workerProfilesCache?.workerProfiles || [],
        loading: false,
        error: errorMessage,
        timestamp: Date.now()
      }
      notifySubscribers()
    }
  }, [mainApi, peopleApi])

  const refresh = useCallback(async () => {
    await fetchAndCache()
  }, [fetchAndCache])

  // Check if data needs to be fetched
  useEffect(() => {
    const now = Date.now()
    
    // Fetch if no cache exists, cache is expired, or cache has an error
    if (!workerProfilesCache || 
        (now - workerProfilesCache.timestamp) > CACHE_TTL ||
        workerProfilesCache.error) {
      fetchAndCache()
    }
  }, [fetchAndCache])

  // Return current cache state or defaults
  const currentCache = workerProfilesCache || {
    workerProfiles: [],
    loading: true,
    error: null,
    timestamp: 0
  }

  return {
    loading: currentCache.loading,
    error: currentCache.error,
    workerProfiles: currentCache.workerProfiles,
    refresh
  }
} 